class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');
    
    this.notification.addEventListener(
      'transitionend',
      () => {
        const container = document.getElementById('cart-notification-total');
        const totalText =
          container?.getElementsByClassName('cart-notification__total-value')[0]?.textContent || '';
        const totalCents = this.parseMoneyToCents(totalText);

        this.updateRewardBar(totalCents);

        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    const productSectionHTML = parsedState.sections?.['cart-notification-product'];
    if (!this.cartItemKey && productSectionHTML) {
      const doc = new DOMParser().parseFromString(productSectionHTML, 'text/html');
      const firstItem = doc.querySelector('[id^="cart-notification-product-"]');
      if (firstItem?.id) {
        this.cartItemKey = firstItem.id.replace('cart-notification-product-', '');
      }
    }
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = document.getElementById(section.id);
      const sectionHTML = parsedState.sections?.[section.id];
      if (!sectionElement || !sectionHTML) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
    });

    const totalContainer = document.getElementById('cart-notification-total');
    const totalSectionHTML = parsedState.sections?.['cart-notification-product'];
    if (totalContainer && totalSectionHTML) {
      const totalHTML = this.getSectionInnerHTML(totalSectionHTML, '#cart-notification-total');
      if (totalHTML) totalContainer.innerHTML = totalHTML;
    }

    if (this.header) this.header.reveal();
    this.syncRewardFromDom();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
      {
        id: 'cart-notification-reward',
        selector: '#cart-notification-reward',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fragment = doc.querySelector(selector);
    return fragment ? fragment.innerHTML : '';
  }

  syncRewardFromDom() {
    const container = document.getElementById('cart-notification-total');
    const totalText =
      container?.getElementsByClassName('cart-notification__total-value')[0]?.textContent || '';
    const totalCents = this.parseMoneyToCents(totalText);
    this.updateRewardBar(totalCents);
  }

  parseMoneyToCents(text) {
    if (!text) return 0;
    const cleaned = text.replace(/[^\d.,]/g, '').replace(/,/g, '');
    if (!cleaned) return 0;
    const parts = cleaned.split('.');
    let cents = parseInt(parts[0] || '0', 10) * 100;
    if (parts[1]) {
      const decimals = parts[1].padEnd(2, '0').slice(0, 2);
      cents += parseInt(decimals, 10);
    }
    return Number.isFinite(cents) ? cents : 0;
  }

  updateRewardBar(totalCents) {
    const rewardEl = document.getElementById('cart-notification-reward');
    if (!rewardEl) return;

    const captions = rewardEl.querySelectorAll('.cart-reward__caption');
    const thresholdFromCaption = (index) => this.parseMoneyToCents(captions[index]?.textContent || '');

    const shipping = Number(rewardEl.dataset.freeShipping || 0) || thresholdFromCaption(0);
    const gift = Number(rewardEl.dataset.freeGift || 0) || thresholdFromCaption(1);
    const discount10 = Number(rewardEl.dataset.discount10 || 0) || thresholdFromCaption(2);
    const discount15 = Number(rewardEl.dataset.discount15 || 0) || thresholdFromCaption(3);

    const exclusiveMax = Math.max(gift, discount10, discount15, 0);
    const barMax = exclusiveMax > 0 ? exclusiveMax : Math.max(shipping, 1);
    const percent = Math.min(100, Math.max(0, (totalCents * 100) / barMax));

    const fillEl = rewardEl.querySelector('.cart-reward__fill');
    if (fillEl) {
      fillEl.style.setProperty('--cart-progress-value', `${percent}%`);
      fillEl.style.width = `${percent}%`;
    }

    const steps = rewardEl.querySelectorAll('.cart-reward__step');
    steps.forEach((step) => step.classList.remove('is-complete'));

    const setPosition = (key, value) => {
      const el = rewardEl.querySelector(`[data-reward-step="${key}"]`);
      if (!el) return;
      const pos = barMax > 0 ? Math.min(100, Math.max(0, (value * 100) / barMax)) : 0;
      el.style.setProperty('--reward-position', `${pos}%`);
      el.style.left = `${pos}%`;
    };

    setPosition('shipping', shipping);
    setPosition('gift', gift);
    setPosition('discount10', discount10);
    setPosition('discount15', discount15);

    if (shipping && totalCents >= shipping) {
      steps[0]?.classList.add('is-complete');
    }

    if (discount15 && totalCents >= discount15) {
      steps[3]?.classList.add('is-complete');
    } else if (discount10 && totalCents >= discount10) {
      steps[2]?.classList.add('is-complete');
    } else if (gift && totalCents >= gift) {
      steps[1]?.classList.add('is-complete');
    }
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
